import { useEffect, useState } from 'react';
import { analyzeSaju, hasLeap } from '../api/client.js';
import { DesktopIcon, DesktopWindow, WindowTitle } from '../components/Desktop.jsx';

// 시진 선택 (네이버 만세력과 같은 구간). 계산에는 구간 가운데 시각을 쓴다.
const SIJIN = [
  ['子(자)', '23:30 ~ 01:29', 0, 30], ['丑(축)', '01:30 ~ 03:29', 2, 30],
  ['寅(인)', '03:30 ~ 05:29', 4, 30], ['卯(묘)', '05:30 ~ 07:29', 6, 30],
  ['辰(진)', '07:30 ~ 09:29', 8, 30], ['巳(사)', '09:30 ~ 11:29', 10, 30],
  ['午(오)', '11:30 ~ 13:29', 12, 30], ['未(미)', '13:30 ~ 15:29', 14, 30],
  ['申(신)', '15:30 ~ 17:29', 16, 30], ['酉(유)', '17:30 ~ 19:29', 18, 30],
  ['戌(술)', '19:30 ~ 21:29', 20, 30], ['亥(해)', '21:30 ~ 23:29', 22, 30],
];
const THIS_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: THIS_YEAR - 1900 + 1 }, (_, i) => THIS_YEAR - i);
const daysIn = (y, m) => new Date(y, m, 0).getDate();

export default function InputPage({ onSubmit }) {
  const [form, setForm] = useState({ calendar: 'SOLAR', leap: false, year: 1998, month: 1, day: 1, sijin: '', gender: 'M' });
  const [leapAvailable, setLeapAvailable] = useState(false);
  const lunar = form.calendar === 'LUNAR';

  // 음력이면 그 해·달에 윤달이 있는지 물어서 '윤달' 체크를 켠다
  useEffect(() => {
    if (!lunar) { setLeapAvailable(false); return; }
    let alive = true;
    hasLeap(+form.year, +form.month).then((v) => {
      if (!alive) return;
      setLeapAvailable(v);
      if (!v) setForm((f) => (f.leap ? { ...f, leap: false } : f));
    }).catch(() => alive && setLeapAvailable(false));
    return () => { alive = false; };
  }, [lunar, form.year, form.month]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [folder, setFolder] = useState(0);
  const [preview, setPreview] = useState(0);
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const previewLines = ['출생 정보를 입력하세요.', '매년 활동을 선택하세요.', '진행 기록을 확인하세요.'];
  useEffect(() => {
    if (!previewPlaying) return undefined;
    const timer = setInterval(() => setPreview((value) => (value + 1) % 3), 3500);
    return () => clearInterval(timer);
  }, [previewPlaying]);
  const folders = [
    ['나의 사주', '사주와 초기 능력치', '생년월일시를 기준으로 계산합니다.'],
    ['인생의 선택', '활동과 선택', '매년 활동을 정하고 사건에 응답합니다.'],
    ['곁의 사람', '만남과 관계', '사람을 만나고 관계를 이어갑니다.'],
    ['나의 기록', '진행 기록', '완료한 인생의 기록과 결과를 확인합니다.'],
  ];

  // 월·년을 바꿨을 때 일 수가 줄어들면 일도 맞춘다
  const change = (k) => (e) => {
    const next = { ...form, [k]: e.target.value };
    const max = next.calendar === 'LUNAR' ? 30 : daysIn(+next.year, +next.month);
    if (+next.day > max) next.day = max;
    setForm(next);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const sj = form.sijin === '' ? null : SIJIN[+form.sijin];
      const body = {
        year: +form.year, month: +form.month, day: +form.day,
        hour: sj ? sj[2] : null, minute: sj ? sj[3] : null,
        gender: form.gender,
        calendar: form.calendar, leap: lunar && form.leap,
      };
      const saju = await analyzeSaju(body);
      onSubmit(body, saju);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const days = Array.from({ length: lunar ? 30 : daysIn(+form.year, +form.month) }, (_, i) => i + 1);

  return (
    <section className="retro-entry">
      <div className="retro-entry-grid">
        <form className="card narrow retro-birth-window" onSubmit={submit} aria-busy={loading}>
          <WindowTitle>출생 정보</WindowTitle>
          <div className="birth-address"><span aria-hidden="true">◇</span><span>내 컴퓨터</span><span>/</span><b>새 인생</b><span className="address-star" aria-hidden="true">☆</span></div>
          <div className="birth-body">
          <div className="birth-heading"><div className="birth-icon"><DesktopIcon kind="moon" /></div><h1>출생 정보</h1></div>
          <p className="birth-description">생년월일과 태어난 시간을 입력하세요.</p>
          <span className="field-caption" id="calendar-label">달력 기준</span>
          <div className="row retro-calendar-row" role="radiogroup" aria-labelledby="calendar-label">
            <label className={!lunar ? 'selected' : ''}><input type="radio" name="cal" checked={!lunar} onChange={() => setForm({ ...form, calendar: 'SOLAR', leap: false })} /> 양력</label>
            <label className={lunar ? 'selected' : ''}><input type="radio" name="cal" checked={lunar} onChange={() => setForm({ ...form, calendar: 'LUNAR' })} /> 음력</label>
            {lunar && (
              <label><input type="checkbox" checked={form.leap} disabled={!leapAvailable}
                onChange={(e) => setForm({ ...form, leap: e.target.checked })} /> 윤달{!leapAvailable && ' (이 달은 없어요)'}</label>
            )}
          </div>
          <div className="row retro-date-row">
            <label>태어난 연도
              <select value={form.year} onChange={change('year')}>
                {YEARS.map((y) => <option key={y} value={y}>{y}년</option>)}
              </select>
            </label>
            <label>월
              <select value={form.month} onChange={change('month')}>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <option key={m} value={m}>{m}월</option>)}
              </select>
            </label>
            <label>일
              <select value={form.day} onChange={change('day')}>
                {days.map((d) => <option key={d} value={d}>{d}일</option>)}
              </select>
            </label>
          </div>
          <div className="row retro-time-row">
            <label>태어난 시간
              <select value={form.sijin} onChange={change('sijin')} aria-describedby="birth-time-hint">
                <option value="">모름</option>
                {SIJIN.map((s, i) => <option key={s[0]} value={i}>{s[0]} {s[1]}</option>)}
              </select>
            </label>
          </div>
          <p className="field-hint" id="birth-time-hint">시간을 모르면 ‘모름’을 선택하세요.</p>
          <span className="field-caption gender-caption" id="gender-label">성별</span>
          <div className="row retro-gender-row" role="radiogroup" aria-labelledby="gender-label">
            <label className={form.gender === 'M' ? 'selected' : ''}><input type="radio" name="g" checked={form.gender === 'M'} onChange={() => setForm({ ...form, gender: 'M' })} /> 남자</label>
            <label className={form.gender === 'F' ? 'selected' : ''}><input type="radio" name="g" checked={form.gender === 'F'} onChange={() => setForm({ ...form, gender: 'F' })} /> 여자</label>
          </div>
          {error && <p className="error" role="alert">{error}</p>}
          <button className="primary birth-submit" disabled={loading}>{loading && <span className="birth-spinner" aria-hidden="true" />}{loading ? '계산 중…' : '시작하기'}{!loading && <span aria-hidden="true">↗</span>}</button>
          <p className="birth-privacy"><svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4.5 7V5a3.5 3.5 0 0 1 7 0v2M3 7h10v7H3Z" /><path d="M8 9.5v2" /></svg> 출생 정보는 사주 계산에만 사용됩니다.</p>
          </div>
          <div className="window-status" role="status"><span><i /> {loading ? '계산 중' : error ? '입력 확인' : '입력 대기'}</span><span>{loading ? 'LOADING…' : error ? 'RETRY' : 'READY'}</span></div>
        </form>
        <aside className="retro-entry-art">
          <DesktopWindow title="나의 인생 폴더" tone="lavender" label="4 ITEMS" className="life-explorer">
            <div className="explorer-toolbar"><span>내 인생 / 구성 요소</span><span className="explorer-view" aria-hidden="true"><i /><i /><i /><i /></span></div>
            <div className="folder-grid">{folders.map(([name], i) => <button key={name} type="button" className={`folder-item ${folder === i ? 'active' : ''}`} onClick={() => setFolder(i)} aria-pressed={folder === i}><DesktopIcon variant={i} /><span>{name}</span></button>)}</div>
            <div className="folder-description" aria-live="polite"><b>{folders[folder][1]}</b><p>{folders[folder][2]}</p></div>
          </DesktopWindow>
          <DesktopWindow title="인생 미리보기" tone="pink" className="life-player">
            <div className="player-body"><div className={`player-screen preview-scene-${preview} ${previewPlaying ? 'is-playing' : ''}`}><span className="player-spark spark-one">✧</span>{preview > 0 && <DesktopIcon kind={['', 'folder', 'mail'][preview]} />}<span className="player-spark spark-two">✧</span><p aria-live={previewPlaying ? 'off' : 'polite'}>{previewLines[preview]}</p></div><div className="player-progress" style={{ '--progress': `${((preview + 1) / 3) * 100}%` }}><span /></div><div className="player-caption"><div className="player-buttons"><button type="button" aria-label={previewPlaying ? '미리보기 일시정지' : '미리보기 재생'} onClick={() => setPreviewPlaying((value) => !value)}><svg viewBox="0 0 20 20" aria-hidden="true">{previewPlaying ? <path d="M6 4h3v12H6Zm6 0h3v12h-3Z" /> : <path d="m5 3 12 7-12 7Z" />}</svg></button><button type="button" aria-label="다음 미리보기" onClick={() => setPreview((value) => (value + 1) % 3)}><svg viewBox="0 0 24 20" aria-hidden="true"><path d="m2 4 9 6-9 6Zm10 0 9 6-9 6Z" /></svg></button></div><span className="preview-count">0{preview + 1} / 03</span></div></div>
          </DesktopWindow>
          <div className="desktop-note"><DesktopIcon kind="mail" /><p>사주는 초기 조건입니다.<br /><b>결과는 선택에 따라 달라집니다.</b></p></div>
        </aside>
      </div>
    </section>
  );
}


